// EN: permission.guard.ts

import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { map, Observable, of } from 'rxjs';
import { DefinedPermission } from '@/common/models/permission.model';
import { PermissionService } from '@/common/services';

// 1. Usa @Injectable() y crea una clase
@Injectable({ providedIn: 'root' })
export class PermissionGuard {
  // 2. Inyecta los servicios en el constructor (forma clásica y robusta)
  constructor(
    private _permissionService: PermissionService,
    private _messageService: MessageService,
    private _translateService: TranslateService,
    private _router: Router,
  ) {}

  // 3. La lógica va dentro de un método que devuelve CanActivateFn
  canActivate(route: ActivatedRouteSnapshot, _: RouterStateSnapshot): Observable<boolean | UrlTree> {
    // 4. Obtenemos los permisos desde la propiedad 'data' de la ruta
    const requiredPermissions = route.data['permissions'] as DefinedPermission[] || [];

    if (requiredPermissions.length === 0) {
      return of(true);
    }

    return this._permissionService.validateRoutePermissions(requiredPermissions).pipe(
      map(hasPermission => {
        if (!hasPermission) {
          this._messageService.add({
            severity: 'error',
            summary: this._translateService.instant('COMMON.COMPONENTS.ACCESS_DENIED_DIALOG.TITLE'),
            detail: this._translateService.instant('COMMON.COMPONENTS.ACCESS_DENIED_DIALOG.MESSAGE'),
            life: 4500,
          });

          // Redirige (en vez de devolver false) para que el Router complete la navegación
          // con NavigationEnd: si solo se cancela (NavigationCancel), el splash de arranque
          // se queda colgado en una carga en frío (ver AppInitializerService).
          return this._router.createUrlTree(['/errors/access']);
        }

        return true;
      }),
    );
  }
}

// 5. Creamos una función de ayuda para usarla en las rutas
export const canActivatePermission: CanActivateFn = (route, state) => inject(PermissionGuard).canActivate(route, state);
